import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Search,
  Filter,
  Package,
  Edit2,
  Check,
  X,
  Loader2,
  Upload,
  Eye,
  DollarSign,
  FileText,
  Clock,
} from 'lucide-react';
import ImageUploadModal from './ImageUploadModal';
import ImageGalleryModal from './ImageGalleryModal';
import PriceEditModal from './PriceEditModal';
import DescriptionEditModal from './DescriptionEditModal';

function InventoryOverview({ userId, refreshKey = 0, onInventoryChanged }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [selectedProductForPrice, setSelectedProductForPrice] = useState(null);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedProductForDescription, setSelectedProductForDescription] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [userId, search, selectedCat, refreshKey]);

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function fetchInventory() {
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        user_id: userId,
        ...(search && { search }),
        ...(selectedCat && { category_id: selectedCat }),
      };
      const { data } = await axios.get(`${baseUrl}/api/inventory`, { params });
      setItems(data.items || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveTitle(productId) {
    if (!editingTitleValue.trim()) {
      alert('El nombre no puede estar vacio');
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${baseUrl}/api/products/${productId}`,
        { title: editingTitleValue.trim() }
      );

      setItems(items.map((row) =>
        row.product_id === productId ? { ...row, title: data.title } : row
      ));
      setEditingTitleId(null);
      setEditingTitleValue('');
      onInventoryChanged?.();
    } catch (error) {
      console.error('Error updating title:', error);
      alert(error.response?.data?.error || 'Error al actualizar nombre');
    } finally {
      setSaving(false);
    }
  }

  function openUploadModal(item) {
    setSelectedProduct({ id: item.product_id, title: item.title, sku: item.sku });
    setUploadModalOpen(true);
  }

  function openGalleryModal(item) {
    setSelectedProduct({ id: item.product_id, title: item.title, sku: item.sku });
    setGalleryModalOpen(true);
  }

  function openPriceModal(item) {
    setSelectedProductForPrice({
      id: item.product_id,
      title: item.title,
      sku: item.sku,
      price: item.price,
    });
    setPriceModalOpen(true);
  }

  function openDescriptionModal(item) {
    setSelectedProductForDescription({
      id: item.product_id,
      title: item.title,
      sku: item.sku,
      description: item.description || '',
    });
    setDescriptionModalOpen(true);
  }

  function handleUploadSuccess() {
    fetchInventory();
    onInventoryChanged?.();
  }

  function handleImagesChanged() {
    fetchInventory();
    onInventoryChanged?.();
  }

  function handlePriceUpdated(productId, newPrice) {
    setItems(items.map((item) =>
      item.product_id === productId ? { ...item, price: newPrice } : item
    ));
    setPriceModalOpen(false);
    setSelectedProductForPrice(null);
    onInventoryChanged?.();
  }

  function handleDescriptionUpdated(productId, newDescription) {
    setItems(items.map((item) =>
      item.product_id === productId ? { ...item, description: newDescription } : item
    ));
    setDescriptionModalOpen(false);
    setSelectedProductForDescription(null);
    onInventoryChanged?.();
  }

  function PendingBadge({ item }) {
    if (!item.pending_quantity) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#E6B43C]/15 text-[#B8860B] border border-[#E6B43C]/30 rounded-full text-xs font-bold font-montserrat">
        <Clock className="w-3 h-3" />
        {item.pending_quantity} pendiente
      </span>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <select
                value={selectedCat}
                onChange={(e) => setSelectedCat(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat appearance-none bg-white cursor-pointer"
              >
                <option value="">Todas las categorias</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
              No se encontraron productos
            </h3>
            <p className="text-sm text-slate-600 font-montserrat">
              {search || selectedCat ? 'Intenta ajustar tus filtros de busqueda' : 'Crea tu primer producto para comenzar'}
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">Producto</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">SKU</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">Categoria</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">Precio</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">Fast Max</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">Contenido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {items.map((item) => (
                    <tr key={item.product_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        {editingTitleId === item.product_id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingTitleValue}
                              onChange={(e) => setEditingTitleValue(e.target.value)}
                              className="flex-1 px-3 py-1.5 border border-brand-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-sm font-montserrat"
                              autoFocus
                              disabled={saving}
                            />
                            <button onClick={() => handleSaveTitle(item.product_id)} disabled={saving} className="p-1.5 bg-brand-green hover:bg-brand-green/90 text-white rounded-lg transition-colors disabled:opacity-50">
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button onClick={() => setEditingTitleId(null)} disabled={saving} className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors disabled:opacity-50">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-900 font-montserrat">{item.title}</span>
                            <button
                              onClick={() => {
                                setEditingTitleId(item.product_id);
                                setEditingTitleValue(item.title);
                              }}
                              className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4"><span className="text-sm text-slate-600 font-montserrat">{item.sku}</span></td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600 font-montserrat">{(item.categories || []).join(', ')}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm font-semibold text-slate-900 font-montserrat">S/ {parseFloat(item.price || 0).toFixed(2)}</span>
                          <button onClick={() => openPriceModal(item)} className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors" title="Editar precio">
                            <DollarSign className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-sm font-bold text-slate-900 font-montserrat">{item.fastmax_quantity || 0} unidades</span>
                          <PendingBadge item={item} />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openDescriptionModal(item)} className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors" title="Editar descripcion">
                            <FileText className="w-4 h-4" />
                          </button>
                          <button onClick={() => openUploadModal(item)} className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors" title="Subir imagenes">
                            <Upload className="w-4 h-4" />
                          </button>
                          <button onClick={() => openGalleryModal(item)} className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors" title="Ver galeria">
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="lg:hidden space-y-4">
            {items.map((item) => (
              <div key={item.product_id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 font-montserrat">{item.title}</p>
                    <p className="text-xs text-slate-500 font-montserrat">{item.sku}</p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingTitleId(item.product_id);
                      setEditingTitleValue(item.title);
                    }}
                    className="p-1.5 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>

                {editingTitleId === item.product_id && (
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="text"
                      value={editingTitleValue}
                      onChange={(e) => setEditingTitleValue(e.target.value)}
                      className="flex-1 px-3 py-2 border border-brand-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-sm font-montserrat"
                    />
                    <button onClick={() => handleSaveTitle(item.product_id)} className="p-2 bg-brand-green text-white rounded-lg">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingTitleId(null)} className="p-2 bg-slate-200 text-slate-700 rounded-lg">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <p className="text-xs text-slate-500 font-montserrat mb-1">Categoria</p>
                    <p className="text-sm text-slate-900 font-montserrat">{(item.categories || []).join(', ') || 'Sin categoria'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-montserrat mb-1">Precio</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-900 font-montserrat">S/ {parseFloat(item.price || 0).toFixed(2)}</p>
                      <button onClick={() => openPriceModal(item)} className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded">
                        <DollarSign className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-slate-500 font-montserrat">Stock confirmado en Fast Max</p>
                    <p className="text-lg font-bold text-slate-900 font-montserrat">{item.fastmax_quantity || 0}</p>
                  </div>
                  <PendingBadge item={item} />
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <button onClick={() => openDescriptionModal(item)} className="flex-1 px-3 py-2 bg-brand-primary/10 text-brand-primary rounded-lg font-semibold font-montserrat hover:bg-brand-primary/20 transition-colors flex items-center justify-center gap-2">
                    <FileText className="w-4 h-4" />
                    Descripcion
                  </button>
                  <button onClick={() => openUploadModal(item)} className="flex-1 px-3 py-2 bg-brand-primary/10 text-brand-primary rounded-lg font-semibold font-montserrat hover:bg-brand-primary/20 transition-colors flex items-center justify-center gap-2">
                    <Upload className="w-4 h-4" />
                    Subir
                  </button>
                  <button onClick={() => openGalleryModal(item)} className="flex-1 px-3 py-2 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors flex items-center justify-center gap-2">
                    <Eye className="w-4 h-4" />
                    Ver
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {uploadModalOpen && selectedProduct && (
        <ImageUploadModal
          productId={selectedProduct.id}
          productTitle={selectedProduct.title}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedProduct(null);
          }}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {galleryModalOpen && selectedProduct && (
        <ImageGalleryModal
          productId={selectedProduct.id}
          productTitle={selectedProduct.title}
          onClose={() => {
            setGalleryModalOpen(false);
            setSelectedProduct(null);
          }}
          onImagesChanged={handleImagesChanged}
        />
      )}

      {priceModalOpen && selectedProductForPrice && (
        <PriceEditModal
          productId={selectedProductForPrice.id}
          productTitle={selectedProductForPrice.title}
          currentPrice={selectedProductForPrice.price}
          onClose={() => {
            setPriceModalOpen(false);
            setSelectedProductForPrice(null);
          }}
          onPriceUpdated={handlePriceUpdated}
        />
      )}

      {descriptionModalOpen && selectedProductForDescription && (
        <DescriptionEditModal
          productId={selectedProductForDescription.id}
          productTitle={selectedProductForDescription.title}
          currentDescription={selectedProductForDescription.description}
          onClose={() => {
            setDescriptionModalOpen(false);
            setSelectedProductForDescription(null);
          }}
          onDescriptionUpdated={handleDescriptionUpdated}
        />
      )}
    </div>
  );
}

export default InventoryOverview;
